import { useEffect, useMemo, useState } from 'react';
import { listSkills, getProject, publishOutcome } from '@/api';
import HostingPaywall from '@/pages/home/components/HostingPaywall';
import { SKILLS, type SkillDef } from '@/mocks/pipeline';
import { toPalette } from '@/pages/home/toPalette';
import ComposeRail from './components/ComposeRail';
import ComposeDrawer from './components/ComposeDrawer';
import SkillsActionList from './components/SkillsActionList';
import PreviewFrame from './components/PreviewFrame';
import { useCompose } from './useCompose';

const DRAWER_KEY = 'sh_compose_drawer';

type Tab = 'chat' | 'skills';
type DrawerState = { open: boolean; tab: Tab };

function loadDrawer(): DrawerState {
  if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID) {
    return { open: true, tab: 'chat' };
  }
  try {
    const raw = localStorage.getItem(DRAWER_KEY);
    if (!raw) return { open: true, tab: 'chat' };
    const parsed = JSON.parse(raw) as { open?: boolean; tab?: string };
    const tab = parsed.tab === 'skills' ? 'skills' : 'chat';
    return { open: parsed.open !== false, tab };
  } catch {
    return { open: true, tab: 'chat' };
  }
}

type Props = {
  projectId: string;
  skills?: SkillDef[];
  onRun?: () => void;
};

export default function ComposeView({ projectId, skills: skillsProp, onRun }: Props) {
  const [{ open, tab }, setDrawer] = useState<DrawerState>(loadDrawer);
  const [draft, setDraft] = useState('');
  const [paywall, setPaywall] = useState(false);
  const [outcomeId, setOutcomeId] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<SkillDef[]>(() =>
    skillsProp !== undefined ? skillsProp : SKILLS,
  );
  const skillMap = useMemo(
    () => Object.fromEntries(catalog.map((s) => [s.id, s])),
    [catalog],
  );
  const compose = useCompose({ projectId, skillMap });

  useEffect(() => {
    try {
      localStorage.setItem(DRAWER_KEY, JSON.stringify({ open, tab }));
    } catch {
      /* ignore */
    }
  }, [open, tab]);

  useEffect(() => {
    if (skillsProp !== undefined) {
      setCatalog(skillsProp);
      return;
    }
    if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID) return;
    let cancelled = false;
    const delaysMs = [0, 400, 800, 1600, 3200];
    void (async () => {
      for (const delay of delaysMs) {
        if (delay) await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        try {
          const data = await listSkills();
          if (!Array.isArray(data) || data.length === 0) continue;
          if (!cancelled) setCatalog(data.map((s) => toPalette(s)));
          return;
        } catch {
          // API often starts after Vite; retry until it listens.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [skillsProp]);

  useEffect(() => {
    if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID) return;
    if (!projectId) return;
    let cancelled = false;
    getProject(projectId)
      .then((p) => {
        if (!cancelled && p.outcome_id) setOutcomeId(p.outcome_id);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const onTab = (next: Tab) => setDrawer((d) => ({ ...d, tab: next }));
  const onToggle = () => setDrawer((d) => ({ ...d, open: !d.open }));

  const insertSkill = (id: string, prefix: string) => {
    setDraft(prefix);
    setDrawer((d) => ({ ...d, tab: 'chat', open: true }));
  };

  const sendDraft = () => {
    const content = draft.trim();
    if (!content) return;
    void compose.send(content).catch(() => undefined);
  };

  const publish = () => {
    if (!outcomeId) {
      setPaywall(true);
      return;
    }
    void publishOutcome(outcomeId).then((res) => {
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      if (res.error) setPaywall(true);
    }).catch(() => setPaywall(true));
  };

  const run = () => {
    onRun?.();
    void compose.run();
  };

  return (
    <div data-testid="compose-root" className="light min-h-screen bg-bg text-textp flex flex-col">
      <header className="h-12 shrink-0 border-b border-border px-4 flex items-center justify-between gap-3">
        <div className="font-semibold tracking-tight">Compose</div>
        <div className="flex items-center gap-2">
          <a href={`/studio?project=${encodeURIComponent(projectId)}`} className="text-sm text-texts hover:text-textp px-3 py-1.5">
            Open in Studio
          </a>
          <button
            type="button"
            data-testid="compose-publish"
            onClick={publish}
            className="text-sm text-texts hover:text-textp px-3 py-1.5"
          >
            Publish
          </button>
          <button
            type="button"
            data-testid="compose-run"
            onClick={run}
            className="text-sm font-medium bg-accent hover:bg-accent-hover text-accent-text px-3 py-1.5 rounded-lg"
          >
            Run
          </button>
        </div>
      </header>
      {paywall && (
        <div className="px-4 py-2 border-b border-border">
          <HostingPaywall onClose={() => setPaywall(false)} />
        </div>
      )}
      <div className="flex-1 flex min-h-0">
        <ComposeRail tab={tab} open={open} onTab={onTab} onToggle={onToggle} />
        <ComposeDrawer open={open} tab={tab} onToggle={onToggle} onTab={onTab}>
          {tab === 'skills' ? (
            <SkillsActionList skills={catalog} onInsert={insertSkill} onInserted={() => onTab('chat')} />
          ) : (
            <div className="h-full flex flex-col p-3 pt-12">
              <textarea
                data-testid="compose-draft"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
                placeholder="Describe an outcome..."
                className="flex-1 w-full bg-surface border border-border rounded-md p-2 text-sm text-textp outline-none focus:border-accent resize-none"
              />
              <button
                type="button"
                data-testid="compose-send"
                onClick={sendDraft}
                className="mt-2 self-end text-sm font-medium bg-accent hover:bg-accent-hover text-accent-text px-3 py-1.5 rounded-lg"
              >
                Send
              </button>
            </div>
          )}
        </ComposeDrawer>
        <div className="flex-1 min-w-0 bg-white">
          <PreviewFrame html={null} />
        </div>
      </div>
    </div>
  );
}
